import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Publications.css';

const Publications = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const publications = [
    {
      title: 'The Method of Finite Difference Regression',
      authors: 'Arjun Banerjee',
      advisor: 'Prof. Emeritus Bradley J. Lucier',
      journal: 'Open Journal of Statistics',
      year: '2018',
      link: 'https://www.scirp.org/journal/paperinformation?paperid=82248',
      videoLink: 'https://www.youtube.com/watch?v=aKRV_3n1adY',
      abstract: 'A novel polynomial regression method for uniformly sampled sequences of noisy data points that determines the order of the best fitting polynomial and provides estimates of its coefficients. Demonstrates how t-test from statistics combined with finite differences yields a more sensitive and objective measure of polynomial order.',
      badge: 'Intel STS Research Report Badge Recipient',
    },
    {
      title: 'The Lean Scrum: Making Scrum More Dynamic for the Entrepreneurial Environment',
      authors: 'Arjun Banerjee',
      advisor: 'Prof. H.E. Dunsmore',
      journal: 'ResearchGate / Purdue Research Fair',
      year: '2018',
      link: 'https://www.researchgate.net/publication/334290247_The_Lean_Scrum_Making_Scrum_More_Dynamic_for_the_Entrepreneurial_Environment',
      abstract: 'A modified Scrum methodology tailored for entrepreneurial and startup environments, combining lean principles with agile development practices to optimize team productivity in resource-constrained settings.',
    },
  ];

  const patents = [
    {
      number: 'US 63602378',
      title: 'Differential Privacy for Classifier Training Data',
      status: 'Filed Nov 2023',
      companies: ['TomtA.ai'],
      description: 'A system and method that makes a public database holding classifier training data differentially private. While individual privacy is maintained, a machine learning classification model with output labels trained by an analytics company through queries made to this database is accurate in spite of said privatization.',
    },
    {
      number: 'US 62822850',
      title: 'Differential Privacy Data Protection System',
      status: 'Filed',
      companies: ['Pridatex Corporation', 'TomtA.ai'],
      description: 'Novel approach to anonymizing datasets using computational Differential Privacy and Machine Learning to transform sensitive data while preserving statistical integrity.',
    },
    {
      number: 'US 62787377',
      title: 'System for Improving Visibility of Symbols on Reflective Screens',
      status: 'Filed Jan 2019',
      companies: ['Visorware'],
      description: 'Software that improves the visibility and power efficiency of vehicle Heads-Up Displays (HUDs) by adjusting projected symbols for optimal visibility against any background.',
    },
  ];

  return (
    <section id="publications" className="publications section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>Publications & Patents</h2>
          <p className="section-subtitle">
            Academic research and intellectual property contributions
          </p>
        </motion.div>

        <div className="pub-patents-grid">
          {/* Publications */}
          <motion.div
            className="pub-section"
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="section-header">
              <div className="header-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                </svg>
              </div>
              <h3>Publications</h3>
            </div>

            {publications.map((pub, index) => (
              <div key={index} className="pub-card">
                <h4 className="pub-title">
                  {pub.title}
                  <div className="pub-links">
                    <a href={pub.link} target="_blank" rel="noopener noreferrer" className="pub-link" title="Read Paper">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    </a>
                    {pub.videoLink && (
                      <a href={pub.videoLink} target="_blank" rel="noopener noreferrer" className="pub-link pub-video-link" title="Watch Video">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="5 3 19 12 5 21 5 3"/>
                        </svg>
                      </a>
                    )}
                  </div>
                </h4>
                {pub.badge && (
                  <span className="pub-badge">{pub.badge}</span>
                )}
                <p className="pub-abstract">{pub.abstract}</p>
                <div className="pub-meta">
                  <span className="pub-authors">{pub.authors}</span>
                  <span className="pub-journal">{pub.journal}, {pub.year}</span>
                  <span className="pub-advisor">Advisor: {pub.advisor}</span>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Patents */}
          <motion.div
            className="patents-section"
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="section-header">
              <div className="header-icon patent-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 16v-4"/>
                  <path d="M12 8h.01"/>
                </svg>
              </div>
              <h3>Patents & IP</h3>
            </div>

            <div className="patents-list">
              {patents.map((patent, index) => (
                <motion.div
                  key={index}
                  className="patent-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1 }}
                >
                  <div className="patent-header">
                    <span className="patent-number">{patent.number}</span>
                    <span className={`patent-status ${patent.status.toLowerCase().replace(' ', '-')}`}>
                      {patent.status}
                    </span>
                  </div>
                  <h4 className="patent-title">{patent.title}</h4>
                  <p className="patent-desc">{patent.description}</p>
                  <div className="patent-companies">
                    {patent.companies.map((company, i) => (
                      <span key={i} className="company-tag">{company}</span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Publications;

